/**
 * StartScreen — Option D from BisaWofa-Start-Screen-Options.docx.
 *
 * The Board's concern was that opening straight into the map made BisaWofa look
 * like any other mapping app. This is the answer that was recommended: the
 * control wheel drawn as a frosted-glass half-circle coming in from the edge,
 * over the user's own area, carrying four choices — Ask a question, Map, Near me
 * and Alerts.
 *
 * Two things are worth knowing about how it is built:
 *
 *   - **The map behind it is the real map.** The blur is a `backdrop-filter`
 *     over the live OpenLayers canvas, not a screenshot. So the neighbourhood
 *     someone sees behind the glass is genuinely theirs, at whatever position
 *     and zoom the app restored, and there is no image to fetch or keep fresh.
 *   - **The arc mirrors with handedness**, like the radial wheel and the layer
 *     dial, by reading `body.bw-left-handed`.
 *
 * Wording is English literals rather than `t()` on purpose, matching the radial
 * wheel: `t()` returns the key itself when a translation is missing, so a
 * missing key would print `start.askQuestion` across the screen. Adding the
 * keys for all six languages is a follow-up, tracked in BACKLOG.md.
 *
 * Usage:
 *   const choice = await openStartScreen({ alertCount: 2 });
 *   // choice is 'ask' | 'map' | 'near' | 'alerts'
 *
 * @module components/StartScreen
 */

const SKIP_KEY = 'bisawofa-skip-start-screen';

/** Whether the user has asked to go straight to the map from now on. */
export function startScreenDisabled() {
  try { return localStorage.getItem(SKIP_KEY) === '1'; } catch (_) { return false; }
}

/** Turn the start screen back on (offered in Settings). */
export function setStartScreenDisabled(off) {
  try { localStorage.setItem(SKIP_KEY, off ? '1' : '0'); } catch (_) {}
}

function greeting(d = new Date()) {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/**
 * Show the start screen. Resolves with the chosen action once it closes.
 *
 * @param {object} [opts]
 * @param {number} [opts.alertCount]  active weather alerts, for the badge
 * @param {string} [opts.alertText]   one-line summary of the top alert
 * @returns {Promise<'ask'|'map'|'near'|'alerts'>}
 */
export function openStartScreen(opts = {}) {
  _ensureStyles();
  const { alertCount = 0, alertText = '' } = opts;
  const left = document.body.classList.contains('bw-left-handed');

  const root = document.createElement('div');
  root.className = 'startscreen' + (left ? ' startscreen-left' : '');
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', 'BisaWofa — what would you like to do?');

  const item = (id, label, hint, cls, svg, badge = 0) => `
    <button type="button" class="ss-item ${cls}" data-action="${id}"
            aria-label="${label}${hint ? ' — ' + hint : ''}">
      <span class="ss-item-circle">${svg}${
        badge > 0 ? `<span class="ss-badge">${badge > 9 ? '9+' : badge}</span>` : ''
      }</span>
      <span class="ss-item-label">${label}</span>
    </button>`;

  // Inline SVG rather than an icon font: the start screen is the very first
  // thing drawn, and a font that has not loaded yet would show empty boxes.
  const icons = {
    ask: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.1A8.4 8.4 0 0 1 4 11.5a8.5 8.5 0 0 1 8.5-8.5 8.4 8.4 0 0 1 8.5 8.5z"/></svg>',
    map: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 3 3 5.5v15L9 18l6 3 6-2.5v-15L15 6z"/><path d="M9 3v15M15 6v15"/></svg>',
    near: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/></svg>',
    alerts: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4 2.5 20h19z"/><path d="M12 10v4M12 17v.01"/></svg>',
  };

  root.innerHTML = `
    <div class="ss-scrim"></div>

    <header class="ss-head">
      <div class="ss-brand">BisaWofa</div>
      <div class="ss-greet">${greeting()}</div>
      ${alertText
        ? `<div class="ss-conditions ss-conditions-alert">${_esc(alertText)}</div>`
        : '<div class="ss-conditions">Local intelligence, clearly defined</div>'}
    </header>

    <div class="ss-arc" aria-hidden="true"></div>

    <div class="ss-items">
      ${item('ask', 'Ask a question', 'search places and your saved data', 'ss-primary', icons.ask)}
      ${item('map', 'Map', 'open the map', '', icons.map)}
      ${item('near', 'Near me', 'find what is close by', '', icons.near)}
      ${item('alerts', 'Alerts', 'weather warnings', '', icons.alerts, alertCount)}

      <form class="ss-ask" novalidate>
        <input id="ss-ask-input" class="ss-ask-input" type="search" autocomplete="off"
               placeholder="Search for a place…"
               aria-label="Search for a place">
        <!-- The app's delegated voice handler (src/voice.js) picks this up by
             class and data-voice-target, so nothing needs re-initialising when
             the start screen is built. It appends the transcript to the field
             rather than submitting, so a mis-hearing can be corrected before
             the question is sent. -->
        <button type="button" class="voice-mic-btn ss-ask-mic"
                data-voice-target="ss-ask-input"
                aria-label="Speak your question" title="Speak">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <rect x="9" y="2" width="6" height="11" rx="3"/>
            <path d="M5 11a7 7 0 0 0 14 0M12 18v3"/>
          </svg>
        </button>
        <button type="submit" class="ss-ask-go" aria-label="Search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"
               stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
        </button>
      </form>
    </div>

    <div class="ss-hub" aria-hidden="true">
      <img class="ss-hub-img" src="${import.meta.env.BASE_URL}img/bw-192x192.png" alt="">
    </div>

    <footer class="ss-foot">
      <span class="ss-offline"><span class="ss-dot"></span>Works offline</span>
      <label class="ss-skip">
        <input type="checkbox" class="ss-skip-box">
        <span>Always start on the map</span>
      </label>
    </footer>
  `;

  document.body.appendChild(root);
  requestAnimationFrame(() => root.classList.add('ss-open'));

  // Nothing behind the start screen should be reachable while it is up.
  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  const form  = root.querySelector('.ss-ask');
  const input = root.querySelector('.ss-ask-input');

  return new Promise((resolve) => {
    let done = false;
    const finish = (action, query = '') => {
      if (done) return;
      done = true;
      if (root.querySelector('.ss-skip-box')?.checked) setStartScreenDisabled(true);
      root.classList.remove('ss-open');
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      // Resolve only once the screen is actually gone. Resolving immediately
      // meant the caller could open a glass panel (z-index 2000) underneath
      // this one (3000) while it was still fading, and the panel would be
      // both dimmed and unclickable for the length of the transition.
      setTimeout(() => { root.remove(); resolve({ action, query }); }, 420);
    };

    /** "Ask a question" opens its own field here rather than sending the user
     *  to the map's search box — the question should be answerable without
     *  leaving this screen. */
    const openAsk = () => {
      root.classList.add('ss-asking');
      setTimeout(() => input.focus(), 260);
    };
    const closeAsk = () => {
      root.classList.remove('ss-asking');
      input.value = '';
    };

    const onAction = (action) => {
      if (action !== 'ask') { finish(action); return; }
      root.classList.contains('ss-asking') ? closeAsk() : openAsk();
    };

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const q = input.value.trim();
      if (!q) { input.focus(); return; }   // nothing typed — stay put
      finish('ask', q);
    });

    root.addEventListener('click', (e) => {
      // The checkbox is a setting, not a way out.
      if (e.target.closest('.ss-skip')) return;
      const btn = e.target.closest('[data-action]');
      if (btn) { onAction(btn.dataset.action); return; }
      // Tapping outside the wheel does nothing: the Map button is the way to
      // the map, and two ways to do the same thing was one too many.
    });

    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      // Escape backs out of the field first, and only then the screen.
      if (root.classList.contains('ss-asking')) closeAsk();
      else finish('map');
    };
    document.addEventListener('keydown', onKey);

    // Focus the first choice so a keyboard or screen-reader user starts inside
    // the dialog rather than behind it.
    setTimeout(() => root.querySelector('.ss-item')?.focus(), 420);
  });
}

// ---------- styles ----------

const RADIUS = 190;    // hub centre → item centre
const ARC_R  = 270;    // the glass half-circle
const HUB_X  = 44;     // hub centre from the docked edge

let _styled = false;
function _ensureStyles() {
  if (_styled) return;
  _styled = true;

  // θ measured CCW from the +x axis, so the four items fan out symmetrically
  // above and below the horizontal, exactly as in the approved mockup.
  const pos = [60, 20, -20, -60].map((deg) => {
    const rad = (deg * Math.PI) / 180;
    return { x: RADIUS * Math.cos(rad), y: -RADIUS * Math.sin(rad) };
  });

  const css = `
    .startscreen {
      position: fixed; inset: 0; z-index: 3000; overflow: hidden;
      opacity: 0; transition: opacity 0.42s ease;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    .startscreen.ss-open { opacity: 1; }

    /* The live map is blurred in place — no screenshot, so what shows through
       is genuinely the user's own area at the restored position. */
    /* Light veil only. The arc does its own blurring, exactly as a glass modal
       does over the map — if this blurred hard, the arc's backdrop-filter would
       have nothing left to work on and the two would not match. */
    .ss-scrim {
      position: absolute; inset: 0;
      backdrop-filter: blur(2px) saturate(115%);
      -webkit-backdrop-filter: blur(2px) saturate(115%);
      background: linear-gradient(180deg,
        rgba(42,10,46,0.52) 0%, rgba(42,10,46,0.14) 28%,
        rgba(26,5,32,0.24) 76%, rgba(26,5,32,0.66) 100%);
    }

    .ss-head { position: absolute; top: 6vh; left: 0; right: 0; text-align: center;
      pointer-events: none; padding: 0 20px; }
    .ss-brand { font-size: 26px; font-weight: 800; color: #fff; letter-spacing: 0.2px;
      text-shadow: 0 2px 10px rgba(0,0,0,0.35); }
    .ss-greet { margin-top: 6px; font-size: 16px; font-weight: 700; color: #fff; opacity: 0.95; }
    .ss-conditions { margin-top: 4px; font-size: 12.5px; color: #FFE96A; }
    .ss-conditions-alert { font-weight: 700; }

    /* glass half-circle, entering from the docked edge */
    .ss-arc {
      position: absolute; top: 50%; left: ${-ARC_R}px;
      width: ${ARC_R * 2}px; height: ${ARC_R * 2}px; margin-top: ${-ARC_R}px;
      border-radius: 50%;
      /* Same recipe as .glass-card in glassForm.js, so the arc reads as the
         same material as every other panel in the app. */
      background: linear-gradient(160deg,
        rgba(255, 255, 255, 0.72) 0%,
        rgba(255, 255, 255, 0.52) 100%);
      -webkit-backdrop-filter: saturate(180%) blur(26px);
      backdrop-filter: saturate(180%) blur(26px);
      border: 1px solid rgba(255, 255, 255, 0.65);
      box-shadow:
        0 18px 50px rgba(20, 8, 24, 0.28),
        inset 0 1px 0 rgba(255, 255, 255, 0.6);
      transform: translateX(-26px); opacity: 0;
      transition: transform 0.5s cubic-bezier(0.22,1,0.36,1), opacity 0.4s ease;
    }
    .ss-open .ss-arc { transform: translateX(0); opacity: 1; }
    .startscreen-left .ss-arc { left: auto; right: ${-ARC_R}px; transform: translateX(26px); }
    .startscreen-left.ss-open .ss-arc { transform: translateX(0); }

    .ss-items { position: absolute; top: 50%; left: ${HUB_X}px; width: 0; height: 0; }
    .startscreen-left .ss-items { left: auto; right: ${HUB_X}px; }

    .ss-item {
      position: absolute; display: flex; flex-direction: column; align-items: center;
      gap: 5px; padding: 0; border: none; background: none; cursor: pointer;
      transform: translate(-50%, -50%) scale(0.4); opacity: 0;
      transition: transform 0.42s cubic-bezier(0.22,1,0.36,1) var(--d, 0ms),
                  opacity 0.3s ease var(--d, 0ms);
      -webkit-tap-highlight-color: transparent;
    }
    .ss-open .ss-item {
      transform: translate(calc(var(--x) - 50%), calc(var(--y) - 50%)) scale(1);
      opacity: 1;
    }
    ${pos.map((p, i) => `
    .ss-item:nth-child(${i + 1}) { --x: ${p.x}px; --y: ${p.y}px; --d: ${i * 55}ms; }
    .startscreen-left .ss-item:nth-child(${i + 1}) { --x: ${-p.x}px; }`).join('')}

    /* The field hangs directly beneath the Ask button (item 1), clear of its
       label — mirrored with the rest when left-handed. */
    .ss-items { --ask-x: ${pos[0].x}px; --ask-y: ${pos[0].y + 62}px; }
    .startscreen-left .ss-items { --ask-x: ${-pos[0].x}px; }

    .ss-item-circle {
      position: relative; width: 58px; height: 58px; border-radius: 50%;
      background: #fff; border: 2.5px solid #556B2F; color: #556B2F;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 3px 12px rgba(0,0,0,0.22);
      transition: transform 0.15s, box-shadow 0.15s;
    }
    .ss-item-circle svg { width: 26px; height: 26px; }
    .ss-item:active .ss-item-circle { transform: scale(0.94); }
    /* Suppress the default ring only where we draw our own, so a browser
       without :focus-visible still shows something. */
    .ss-item:focus-visible { outline: none; }
    .ss-item:focus-visible .ss-item-circle { outline: 3px solid #FFE96A; outline-offset: 3px; }

    /* Ask a question leads, so it is larger and carries the accent. */
    .ss-primary .ss-item-circle {
      width: 68px; height: 68px; background: #FFE96A; border-color: #FFE96A; color: #5a4a00;
      box-shadow: 0 4px 18px rgba(255,233,106,0.5);
    }
    .ss-primary .ss-item-circle svg { width: 30px; height: 30px; }

    .ss-badge {
      position: absolute; top: -3px; right: -3px; min-width: 21px; height: 21px;
      padding: 0 5px; border-radius: 11px; background: #dc2626; color: #fff;
      border: 2px solid #fff; font-size: 11px; font-weight: 800;
      display: flex; align-items: center; justify-content: center;
    }

    .ss-item-label {
      background: rgba(255,255,255,0.97); color: #1c1020;
      font-size: 11px; font-weight: 700; line-height: 1.3;
      padding: 3px 9px 4px; border-radius: 7px; white-space: nowrap;
      box-shadow: 0 1px 3px rgba(0,0,0,0.16);
    }

    /* The ask field shoots out from under its button. Anchored to the same
       origin as the items, so it tracks the button on either side. */
    .ss-ask {
      position: absolute; display: flex; align-items: center; gap: 6px;
      left: var(--ask-x); top: var(--ask-y);
      width: 276px; max-width: calc(100vw - 84px);
      padding: 5px 5px 5px 14px; border-radius: 999px;
      background: rgba(255, 255, 255, 0.86);
      -webkit-backdrop-filter: saturate(180%) blur(20px);
      backdrop-filter: saturate(180%) blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.7);
      box-shadow: 0 10px 26px rgba(20, 8, 24, 0.26);
      transform: translate(-50%, -8px) scaleY(0.6);
      transform-origin: top center;
      opacity: 0; pointer-events: none;
      transition: transform 0.26s cubic-bezier(0.22,1,0.36,1), opacity 0.2s ease;
    }
    .ss-asking .ss-ask {
      transform: translate(-50%, 0) scaleY(1); opacity: 1; pointer-events: auto;
    }
    .ss-ask-input {
      flex: 1 1 auto; min-width: 0; border: none; background: none; outline: none;
      font-size: 14px; color: #1c1020; padding: 8px 0;
    }
    .ss-ask-input::placeholder { color: #9c8ba6; }
    .ss-ask-go {
      flex: 0 0 auto; width: 34px; height: 34px; border: none; border-radius: 50%;
      background: linear-gradient(150deg, #8B008B, #a8259c); color: #fff;
      display: flex; align-items: center; justify-content: center; cursor: pointer;
      -webkit-tap-highlight-color: transparent;
    }
    .ss-ask-go svg { width: 17px; height: 17px; }
    .ss-ask-go:active { transform: scale(0.94); }

    /* The mic inherits .voice-mic-btn's behaviour, including the .listening
       pulse, but is restyled to sit inside this field rather than the dock. */
    .ss-ask-mic {
      flex: 0 0 auto; width: 34px; height: 34px; min-width: 0; min-height: 0;
      padding: 0; border: none; border-radius: 50%;
      background: rgba(139, 0, 139, 0.10); color: #8B008B;
      display: flex; align-items: center; justify-content: center; cursor: pointer;
      font-size: 0; line-height: 0;
    }
    .ss-ask-mic svg { width: 17px; height: 17px; }
    .ss-ask-mic.listening { background: #8B008B; color: #fff; }

    /* While the field is open the other choices step out of the way entirely.
       Dimming them was not enough: the field hangs over where the Map button
       sits, and a half-visible button under an input reads as a mistake.
       Tapping Ask again, or Escape, brings them back. */
    .ss-asking .ss-item:not(.ss-primary) {
      opacity: 0; pointer-events: none;
      transform: translate(calc(var(--x) - 50%), calc(var(--y) - 50%)) scale(0.86);
    }

    .ss-hub {
      position: absolute; top: 50%; left: ${HUB_X}px;
      width: 70px; height: 70px; margin: -35px 0 0 -35px; border-radius: 50%;
      background: #fff; border: 3px solid #FFE96A; overflow: hidden; padding: 0;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 4px 16px rgba(0,0,0,0.28);
    }
    .startscreen-left .ss-hub { left: auto; right: ${HUB_X}px; margin-right: -35px; }
    .ss-hub-img {
      width: 100%; height: 100%; object-fit: cover; border-radius: 50%; display: block;
    }

    .ss-foot {
      position: absolute; left: 0; right: 0; bottom: 4vh;
      display: flex; flex-direction: column; align-items: center; gap: 12px;
    }
    .ss-offline {
      display: inline-flex; align-items: center; gap: 7px;
      background: #556B2F; color: #fff; font-size: 11.5px; font-weight: 700;
      padding: 5px 14px; border-radius: 999px;
    }
    .ss-dot { width: 8px; height: 8px; border-radius: 50%; background: #FFE96A; }
    .ss-skip {
      display: inline-flex; align-items: center; gap: 9px; cursor: pointer;
      background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.32);
      color: #fff; font-size: 12px; padding: 8px 17px; border-radius: 999px;
      -webkit-tap-highlight-color: transparent;
    }
    .ss-skip-box { width: 15px; height: 15px; accent-color: #FFE96A; margin: 0; }

    /* Short screens: pull the arc in so nothing runs off the top or bottom. */
    @media (max-height: 700px) {
      .ss-head { top: 3vh; }
      .ss-item-circle { width: 50px; height: 50px; }
      .ss-primary .ss-item-circle { width: 58px; height: 58px; }
      /* The ask field shoots out from under its button. Anchored to the same
       origin as the items, so it tracks the button on either side. */
    .ss-ask {
      position: absolute; display: flex; align-items: center; gap: 6px;
      left: var(--ask-x); top: var(--ask-y);
      width: 276px; max-width: calc(100vw - 84px);
      padding: 5px 5px 5px 14px; border-radius: 999px;
      background: rgba(255, 255, 255, 0.86);
      -webkit-backdrop-filter: saturate(180%) blur(20px);
      backdrop-filter: saturate(180%) blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.7);
      box-shadow: 0 10px 26px rgba(20, 8, 24, 0.26);
      transform: translate(-50%, -8px) scaleY(0.6);
      transform-origin: top center;
      opacity: 0; pointer-events: none;
      transition: transform 0.26s cubic-bezier(0.22,1,0.36,1), opacity 0.2s ease;
    }
    .ss-asking .ss-ask {
      transform: translate(-50%, 0) scaleY(1); opacity: 1; pointer-events: auto;
    }
    .ss-ask-input {
      flex: 1 1 auto; min-width: 0; border: none; background: none; outline: none;
      font-size: 14px; color: #1c1020; padding: 8px 0;
    }
    .ss-ask-input::placeholder { color: #9c8ba6; }
    .ss-ask-go {
      flex: 0 0 auto; width: 34px; height: 34px; border: none; border-radius: 50%;
      background: linear-gradient(150deg, #8B008B, #a8259c); color: #fff;
      display: flex; align-items: center; justify-content: center; cursor: pointer;
      -webkit-tap-highlight-color: transparent;
    }
    .ss-ask-go svg { width: 17px; height: 17px; }
    .ss-ask-go:active { transform: scale(0.94); }

    /* The mic inherits .voice-mic-btn's behaviour, including the .listening
       pulse, but is restyled to sit inside this field rather than the dock. */
    .ss-ask-mic {
      flex: 0 0 auto; width: 34px; height: 34px; min-width: 0; min-height: 0;
      padding: 0; border: none; border-radius: 50%;
      background: rgba(139, 0, 139, 0.10); color: #8B008B;
      display: flex; align-items: center; justify-content: center; cursor: pointer;
      font-size: 0; line-height: 0;
    }
    .ss-ask-mic svg { width: 17px; height: 17px; }
    .ss-ask-mic.listening { background: #8B008B; color: #fff; }

    /* While the field is open the other choices step out of the way entirely.
       Dimming them was not enough: the field hangs over where the Map button
       sits, and a half-visible button under an input reads as a mistake.
       Tapping Ask again, or Escape, brings them back. */
    .ss-asking .ss-item:not(.ss-primary) {
      opacity: 0; pointer-events: none;
      transform: translate(calc(var(--x) - 50%), calc(var(--y) - 50%)) scale(0.86);
    }

    .ss-hub { width: 58px; height: 58px; margin: -29px 0 0 -29px; }
      .startscreen-left .ss-hub { margin-right: -29px; }
    }
    @media (prefers-reduced-motion: reduce) {
      .startscreen, .ss-arc, .ss-item { transition-duration: 0.01ms !important; }
      .ss-item { transition-delay: 0ms !important; }
    }
  `;
  const el = document.createElement('style');
  el.id = 'start-screen-styles';
  el.textContent = css;
  document.head.appendChild(el);
}

function _esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

export default { openStartScreen, startScreenDisabled, setStartScreenDisabled };
